import './CompanyContactInfoModal.scss';

import { CopyOutlined, ExportOutlined } from '@ant-design/icons';
import { useOne } from '@refinedev/core';
import { Alert, Button, Flex, message, Modal, Skeleton, Table, Tooltip, Typography } from 'antd';
import { cloneElement, ReactElement, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ADMIN_URL, COMPANY_URLS } from '@/constants/urls';
import Company, { CompanyContactTypeString } from '@/model/General/Company';

import { useRepositoriesContext } from '../../repositories/RepositoriesContext';

interface CompanyContactInfoModalProps {
  company: Company | number;
  openButton: ReactElement;
  canSelect?: boolean;
  onSelect?: (data) => void;
}

const CompanyContactInfoModal = (props: CompanyContactInfoModalProps) => {
  const { t } = useTranslation();
  const { company, openButton } = props;
  const [openModal, setOpenModal] = useState(false);
  const openButtonModiffied = useMemo(
    () => cloneElement(openButton, { onClick: () => setOpenModal(true) }),
    [openButton],
  );
  const title = useMemo(() => t('Información de contacto'), [t]);

  return (
    <div className="CompanyContactInfoModal">
      <Tooltip title={t('Ver información de contacto')}>{openButtonModiffied}</Tooltip>
      <Modal
        title={title}
        open={openModal}
        onCancel={() => setOpenModal(false)}
        onOk={() => setOpenModal(false)}
        width={1000}
        okText={t('Cerrar')}
        cancelButtonProps={{ style: { display: 'none' } }}
        footer={null}>
        <ModalContent companyToShow={company} openModal={openModal} setOpenModal={setOpenModal} />
      </Modal>
    </div>
  );
};

interface ModalContentProps {
  companyToShow: Company | number;
  openModal: boolean;
  setOpenModal: (open: boolean) => void;
}

const ModalContent = (props: ModalContentProps) => {
  const { companyRepository } = useRepositoriesContext();
  const [contactData, setContactData] = useState([]);
  const [company, setCompany] = useState<Company>();
  const [loadingContact, setLoadingContact] = useState<boolean>();
  const { t } = useTranslation();
  const { companyToShow, openModal, setOpenModal } = props;
  const {
    result,
    query: { isLoading },
  } = useOne({
    resource: 'empresas',
    id: typeof companyToShow === 'number' ? companyToShow : undefined,
    queryOptions: { enabled: typeof companyToShow === 'number' },
  });

  useEffect(() => {
    if (typeof companyToShow === 'number' && result) {
      setCompany(result as Company);
    } else if (typeof companyToShow !== 'number') {
      setCompany(companyToShow);
    }
  }, [companyToShow, result]);

  useEffect(() => {
    if (!company) {
      return;
    }

    const getContactData = async () => {
      const contactData = await companyRepository.getCompanyContact(company.id);

      setContactData(contactData);
      setLoadingContact(false);
    };

    if (openModal && company) {
      setLoadingContact(true);
      void getContactData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  return (
    <>
      {company && !loadingContact && (
        <div className="CompanyContactInfoModalContent">
          <Typography.Text type="secondary" strong>
            {t('Datos de contacto de {{company}}', { company: company.full_name })}
          </Typography.Text>
          {contactData.length > 0 ? (
            <Table dataSource={contactData} scroll={{ x: 800 }} pagination={false} rowKey="id">
              <Table.Column dataIndex={'name'} title={t('Nombre')} />
              <Table.Column dataIndex={'phone'} title={t('Teléfono')} />
              <Table.Column dataIndex={'email'} title={t('Email')} />
              <Table.Column
                dataIndex={'contact_type'}
                title={t('Tipo de contacto')}
                render={v => (v == undefined ? '-' : CompanyContactTypeString[v])}
              />
              <Table.Column dataIndex={'observation'} title={t('Observación')} />
              <Table.Column
                width={30}
                render={(_, record) => {
                  return (
                    <Button
                      className="ButtonCopy"
                      icon={<CopyOutlined />}
                      size="small"
                      onClick={() => {
                        navigator.clipboard
                          .writeText(`${record.name} ${record.phone ? `(${record.phone})` : ''}`)
                          .then(() => {
                            message.success('Copiado al portapapeles');
                          })
                          .catch(err => {
                            console.error('Falló al copiar el texto: ', err);
                          });
                      }}
                    />
                  );
                }}
              />
            </Table>
          ) : (
            <Alert showIcon type="warning" message={t('No hay datos de contacto disponibles')} />
          )}
        </div>
      )}
      {(isLoading || loadingContact) && <Skeleton active />}
      {company ? (
        <Flex gap={8} justify="flex-end" style={{ marginTop: 16 }}>
          <Button
            icon={<ExportOutlined />}
            onClick={() => window.open(`${ADMIN_URL}${COMPANY_URLS.ADMIN_EMPRESA}/${company?.id}`)}>
            {t('Verificar datos de contacto')}
          </Button>
          <Button type="primary" onClick={() => setOpenModal(false)}>
            {t('Cerrar')}
          </Button>
        </Flex>
      ) : null}
    </>
  );
};

export default CompanyContactInfoModal;
